import { useState } from "react";
import {
  Box,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Button,
  Typography,
  Alert,
} from "@mui/material";
import {
  COUNTRIES,
  regionsFor,
  regionLabel,
  isCompleteBillingAddress,
  type BillingAddress,
} from "../../../src/shared/billing-location";
import { saveBillingAddress } from "../api/dashboard";

/**
 * Full billing address, with its own save.
 *
 * Every field except the name and line 2 is required because Helcim's customer
 * record demands them and rejects a blank as hard as an absence — a partial
 * address cannot be padded into an acceptable one, so checkout refuses to
 * attach a card without a complete one. The name is the exception: checkout's
 * validator excludes it, and the contactName Helcim insists on comes from the
 * customer record instead. HelcimPay asks for the street and postal code inside
 * its own iframe for card AVS, but never attaches them to the customer and
 * never returns them, so nothing can be reused from there.
 */
export interface BillingAddressFormProps {
  value: Partial<BillingAddress>;
  onChange: (next: Partial<BillingAddress>) => void;
  /** Raised once the address is accepted, so the caller can enable checkout. */
  onSaved: (address: BillingAddress) => void;
  disabled?: boolean;
}

export default function BillingAddressForm({
  value,
  onChange,
  onSaved,
  disabled,
}: BillingAddressFormProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const country = value.country ?? "";
  const regions = country ? regionsFor(country) : null;
  const complete = isCompleteBillingAddress(value);

  // Any edit invalidates a previous save: the stored address no longer matches
  // what is on screen, and checkout would otherwise proceed against the old one.
  const set = (patch: Partial<BillingAddress>) => {
    setSaved(false);
    setError(null);
    onChange({ ...value, ...patch });
  };

  const handleSave = async () => {
    if (!isCompleteBillingAddress(value)) return;
    setSaving(true);
    setError(null);
    try {
      await saveBillingAddress(value);
      setSaved(true);
      onSaved(value);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not save your billing address",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle2">Billing address</Typography>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 2 }}>
        Your billing address is used <strong>only</strong> to calculate sales tax on your
        subscription. It is never used for anything else.
      </Typography>

      <Stack spacing={2}>
        {/* Genuinely optional, unlike the fields below it. Checkout's
            validator excludes the name, and Helcim's required contactName is
            taken from the customer record rather than from this address — so
            marking it required showed an asterisk on a field that Save has
            never actually waited for. */}
        <TextField
          label="Name on the account (optional)"
          size="small"
          fullWidth
          value={value.name ?? ""}
          disabled={disabled || saving}
          onChange={(e) => set({ name: e.target.value })}
        />
        <TextField
          label="Street address"
          size="small"
          fullWidth
          required
          value={value.street1 ?? ""}
          disabled={disabled || saving}
          onChange={(e) => set({ street1: e.target.value })}
        />
        <TextField
          label="Apartment, suite, etc. (optional)"
          size="small"
          fullWidth
          value={value.street2 ?? ""}
          disabled={disabled || saving}
          onChange={(e) => set({ street2: e.target.value })}
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="City"
            size="small"
            fullWidth
            required
            value={value.city ?? ""}
            disabled={disabled || saving}
            onChange={(e) => set({ city: e.target.value })}
          />
          <TextField
            label="Postal code"
            size="small"
            fullWidth
            required
            value={value.postalCode ?? ""}
            disabled={disabled || saving}
            onChange={(e) => set({ postalCode: e.target.value })}
          />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            select
            label="Country"
            size="small"
            fullWidth
            required
            value={country}
            disabled={disabled || saving}
            // Clearing the region is deliberate: a state code from the previous
            // country is a real-looking but wrong tax jurisdiction.
            onChange={(e) => set({ country: e.target.value, province: "" })}
          >
            {COUNTRIES.map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select={Boolean(regions)}
            label={regionLabel(country)}
            size="small"
            fullWidth
            required
            value={value.province ?? ""}
            disabled={disabled || saving || !country}
            onChange={(e) => set({ province: e.target.value })}
          >
            {regions?.map((r) => (
              <MenuItem key={r.code} value={r.code}>
                {r.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={disabled || saving || !complete}
        >
          {saving ? "Saving..." : "Save billing address"}
        </Button>
        {saved && !error && (
          <Typography variant="body2" color="text.secondary">
            Saved.
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
